/**
 * HMAC Request Signing Utility - Frontend
 *
 * Provides HMAC-SHA256 signature generation for API requests.
 * Works with the backend HMAC verification middleware.
 *
 * @module src/utils/hmacSigning
 */

import { v4 as uuidv4 } from 'uuid';
import Logger from './logger';

// Constants (must match backend)
const SIGNATURE_HEADER = 'x-request-signature';
const TIMESTAMP_HEADER = 'x-request-timestamp';
const NONCE_HEADER = 'x-request-nonce';
const ALGORITHM_HEADER = 'x-signature-algorithm';
const HMAC_ALGORITHM = 'sha256';

/**
 * Generate HMAC signature for a request using Web Crypto API
 *
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE)
 * @param {string} path - Request path (e.g., /api/schedules)
 * @param {string} timestamp - Request timestamp (Unix timestamp in ms)
 * @param {string|object} body - Request body (string or object to be stringified)
 * @param {string} secret - HMAC signing key
 * @param {string} [nonce] - Optional nonce for additional replay attack prevention
 * @returns {Promise<string>} Base64-encoded HMAC signature
 */
async function generateSignature(method, path, timestamp, body, secret, nonce = '') {
  // Normalize body to string
  const bodyString = typeof body === 'string' ? body : JSON.stringify(body || '');

  // Create signature payload: METHOD|PATH|TIMESTAMP|NONCE|BODY
  const payload = `${method.toUpperCase()}|${path}|${timestamp}|${nonce}|${bodyString}`;

  // Convert secret and payload to ArrayBuffer
  const encoder = new TextEncoder();
  const secretBuffer = encoder.encode(secret);
  const payloadBuffer = encoder.encode(payload);

  // Import key for HMAC
  const key = await crypto.subtle.importKey('raw', secretBuffer, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);

  // Generate HMAC signature
  const signatureBuffer = await crypto.subtle.sign('HMAC', key, payloadBuffer);

  // Convert to base64
  const signatureArray = Array.from(new Uint8Array(signatureBuffer));
  const signatureBase64 = btoa(String.fromCharCode(...signatureArray));

  return signatureBase64;
}

/**
 * Sign an API request with HMAC signature
 *
 * @param {object} config - Axios request config
 * @param {string} secret - HMAC signing key
 * @param {object} [options] - Signing options
 * @param {boolean} [options.useNonce=true] - Include nonce for replay attack prevention
 * @returns {Promise<object>} Updated config with signature headers
 */
export async function signRequest(config, secret, options = {}) {
  const { useNonce = true } = options;

  // Skip if no secret provided
  if (!secret) {
    Logger.warn('[HMAC] No signing key provided, request will not be signed');
    return config;
  }

  try {
    // Generate timestamp
    const timestamp = Date.now().toString();

    // Generate nonce if requested
    const nonce = useNonce ? uuidv4() : '';

    // Extract path from URL (remove query params for signature)
    let path = config.url || '';
    // If path is a full URL, extract the pathname; otherwise, use as-is
    if (path.startsWith('http')) {
      const url = new URL(path);
      path = url.pathname;
    }

    // Generate signature
    const signature = await generateSignature(config.method || 'GET', path, timestamp, config.data, secret, nonce);

    // Add headers to config
    config.headers = config.headers || {};
    config.headers[SIGNATURE_HEADER] = signature;
    config.headers[TIMESTAMP_HEADER] = timestamp;
    config.headers[ALGORITHM_HEADER] = HMAC_ALGORITHM;

    if (nonce) {
      config.headers[NONCE_HEADER] = nonce;
    }

    return config;
  } catch (error) {
    Logger.error('[HMAC] Failed to sign request:', error);
    throw new Error('Failed to generate request signature');
  }
}

/**
 * Check if HMAC signing is enabled
 *
 * @returns {boolean} True if HMAC signing is enabled
 */
export function isHmacSigningEnabled() {
  return import.meta.env.VITE_ENABLE_HMAC_SIGNING === 'true';
}

/**
 * Get HMAC signing key from environment
 *
 * @returns {string|null} HMAC signing key or null if not configured
 */
export function getHmacSigningKey() {
  return import.meta.env.VITE_HMAC_SIGNING_KEY || null;
}

/**
 * Constants export for use in other modules
 */
export const HMAC_CONSTANTS = {
  SIGNATURE_HEADER,
  TIMESTAMP_HEADER,
  NONCE_HEADER,
  ALGORITHM_HEADER,
  HMAC_ALGORITHM
};

export default {
  signRequest,
  isHmacSigningEnabled,
  getHmacSigningKey,
  HMAC_CONSTANTS
};
